/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, ChevronLeft, Clock, Lock, ArrowRight, Settings as SettingsIcon, CheckCircle2 } from "lucide-react";
import { formatDT } from "@/lib/format";
import { useTrialCountdown } from "@/lib/useTrialCountdown";
import { StripeCheckoutForm } from "@/components/StripeCheckoutForm";
import {
  MONTHLY_PRICE_EUR,
  YEARLY_PRICE_EUR,
  YEARLY_MONTHLY_EQUIV,
  YEARLY_SAVINGS_EUR,
} from "@/lib/pricing";

// Phase-1 upgrade page. The "Subscribe" button opens a Stripe Elements card
// capture flow; on success we flip to a clear confirmation state with
// explicit navigation back into the app (no dead-ends).
export default function UpgradePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [subBust, setSubBust] = useState(0);
  // v77.5 — Track the just-subscribed moment locally so we can render a
  // bespoke success card with explicit "Return to dashboard" / "Go to
  // settings" CTAs. AuthContext.refresh() will eventually flip
  // `user.plan.plan` to "paid", but we don't want to wait on that
  // round-trip — the user expects an instant confirmation.
  const [justSubscribed, setJustSubscribed] = useState(false);
  // v77.99.10 — Plan-interval selector. Default monthly; persisted in
  // the URL so a deep-link from Pricing (`?plan=yearly`) lands on the
  // yearly card with no extra click.
  const [planInterval, setPlanInterval] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("plan");
      return p === "yearly" ? "yearly" : "monthly";
    } catch (_) { return "monthly"; }
  });

  // Reactivate-in-one-click surface — Iter 123.reactivate.
  // When the sign-in flow lands a locked user here with
  // `?reactivate=1`, we swap the page header for a warm
  // "Welcome back" callout that leads with a single primary
  // action and skips the marketing preamble. The Stripe form
  // below is unchanged, so success flips them to Paid in the
  // same session.
  const isReactivateFlow = (() => {
    try {
      return new URLSearchParams(window.location.search).get("reactivate") === "1";
    } catch (_) { return false; }
  })();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/subscription/me");
        if (!cancelled) setSub(data);
      } catch (err) {
        // Non-fatal — the page still renders the marketing copy. Logged so
        // dev tools / monitoring catch persistent failures.
        console.warn("[upgrade] subscription/me failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [subBust]);

  // v77.11 — Log the utm_source that brought the user here so we can
  // split conversion analytics by entry point (dashboard card vs top
  // banner vs email vs direct). Fires once per mount; failures are
  // silently swallowed — analytics must never block the UI.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const src = params.get("utm_source") || "direct";
      api.post("/analytics/upgrade-cta", { utm_source: src }).catch(() => {});
    } catch (e) { console.debug("[upgrade] analytics fire-and-forget failed:", e?.message); }
  }, []);

  const plan = user?.plan || sub?.plan;
  const trialEnd = plan?.trial_end_at || sub?.trial_end_at;
  // v77.5 — Live countdown: re-renders this component every minute so
  // "3 days left" → "2 days left" → "23 hours left" → "1 hour left" all
  // happen without a manual refresh. Always derived from `trial_end_at`
  // — there is no static days-left number stored anywhere.
  const trialRemaining = useTrialCountdown(plan?.plan === "trial" ? trialEnd : null);
  const isAlreadyPaid = plan?.plan === "paid" || plan?.plan === "complimentary" || plan?.plan === "admin";
  // Show the success card if either the user just subscribed in this
  // session OR /auth/me has already caught up to "paid" — covers the
  // race where Stripe webhook beats us to it.
  const showSuccess = justSubscribed || (justSubscribed === false && plan?.plan === "paid" && !!sub?.subscription_status);

  const handlePaymentSuccess = async () => {
    setJustSubscribed(true);
    // Refetch /auth/me so the rest of the app (banners, gates) picks up
    // the new plan immediately.
    try { await refresh(); } catch (err) { console.warn("[upgrade] refresh failed:", err); }
    setSubBust((n) => n + 1);
  };

  // v77.5 — Post-payment success state. Replaces the whole page with a
  // dedicated confirmation card and explicit navigation. The Stripe
  // checkout form (and its floating "Pay" button) are NOT mounted in
  // this branch, so there's no leftover payment UI hanging around.
  if (justSubscribed) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="upgrade-success">
        <div className="border border-[#1F9FA4] bg-[#E0F2F2] rounded-md p-6 sm:p-8 text-center" data-testid="upgrade-success-card">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#1F9FA4] text-white inline-flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7" strokeWidth={2} />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#0B4D50] mt-4" data-testid="upgrade-success-heading">
            {isReactivateFlow ? "Welcome back — your account is active" : "You're on the Paid plan"}
          </h1>
          <ul className="mt-4 inline-block text-left text-sm text-[#0B4D50] space-y-1.5">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2} />{" "}
              {isReactivateFlow ? "Full read/write access is unlocked right now." : "Paid plan is active right now."}
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2} />{" "}
              {isReactivateFlow ? "Every record you left is exactly where you left it." : "Your first receipt will arrive by email."}
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2} />{" "}
              {isReactivateFlow ? "Exports, downloads, Court Bundles and external shares are enabled again." : "The next billing date will appear on your Settings page."}
            </li>
          </ul>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            type="button"
            onClick={() => navigate("/app/dashboard")}
            className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm h-11 px-5"
            data-testid="upgrade-success-dashboard-button"
          >
            Return to dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/app/settings")}
            className="border-[#D9E1E3] text-[#2E3A3F] rounded-sm h-11 px-5"
            data-testid="upgrade-success-settings-button"
          >
            <SettingsIcon className="w-4 h-4 mr-2" /> Go to settings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="upgrade-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-[#6F828A] hover:text-[#2E3A3F] inline-flex items-center gap-1.5"
        data-testid="upgrade-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="space-y-2">
        {isReactivateFlow && plan?.plan !== "paid" ? (
          <div
            className="border border-[#1F9FA4] bg-[#E0F2F2] rounded-md p-4 sm:p-5"
            data-testid="upgrade-reactivate-banner"
          >
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-full bg-[#1F9FA4] text-white inline-flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4" strokeWidth={2} />
              </span>
              <div className="min-w-0 flex-1">
                <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#0B4D50]" data-testid="upgrade-reactivate-heading">
                  Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
                </h1>
                <p className="text-sm text-[#0B4D50] mt-1">
                  Your account was locked but your data is safe and preserved indefinitely — every record you left off with is still here. Reactivate below and you&apos;re back to full read/write access in the same session.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#2E3A3F]">Upgrade to Paid</h1>
            <p className="text-[#6F828A] text-base">
              Keep full access to messaging, expenses, calendar, requests, documents, and PDF exports.
            </p>
          </>
        )}
      </div>

      {/* Current state card */}
      <div className="border border-[#D9E1E3] bg-white rounded-md p-5" data-testid="upgrade-current-state">
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-sm bg-[#EEF3F4] text-[#6F828A] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
            {plan?.plan === "free" ? <Lock className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A]">Current plan</div>
            <div className="font-heading text-lg font-medium text-[#2E3A3F] capitalize">{plan?.plan || "…"}</div>
            {plan?.plan === "trial" && (
              <p className="text-sm text-[#2E3A3F] mt-1" data-testid="upgrade-trial-remaining">
                <span className="font-medium">{trialRemaining || "Trial ending soon"}</span>
                {trialEnd && <> — trial ends {formatDT(trialEnd)}.</>}
              </p>
            )}
            {plan?.plan === "free" && (
              <p className="text-sm text-[#8F6B1F] mt-1">Your trial has ended — you're in a 7-day read-only grace period. You can sign in and view every record; subscribe before it ends to keep adding new entries.</p>
            )}
            {plan?.plan === "locked" && (
              <p className="text-sm text-[#8C2A22] mt-1">Your account is locked. Sign-in still works and your data is preserved indefinitely — subscribe to add new entries and unlock exports again.</p>
            )}
            {plan?.plan === "complimentary" && (
              <p className="text-sm text-[#116B6E] mt-1">You have complimentary premium access{plan.access_until ? ` until ${formatDT(plan.access_until)}` : " — lifetime"}.</p>
            )}
            {plan?.plan === "paid" && (
              <p className="text-sm text-[#116B6E] mt-1">You're already on the Paid plan. Nothing to do here.</p>
            )}
            {plan?.plan === "admin" && (
              <p className="text-sm text-[#116B6E] mt-1">Admin account — full access regardless of subscription.</p>
            )}
          </div>
        </div>
      </div>

      {/* Plan toggle + price card. v77.99.10 — adds yearly option. */}
      <div className="border-2 border-[#1F9FA4] bg-white rounded-md p-6 space-y-5" data-testid="upgrade-price-card">
        <div className="space-y-4">
          <div
            role="tablist"
            aria-label="Billing cadence"
            className="inline-flex p-1 rounded-md bg-[#EEF3F4] border border-[#D9E1E3]"
            data-testid="upgrade-plan-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={planInterval === "monthly"}
              onClick={() => setPlanInterval("monthly")}
              className={`px-4 h-9 text-sm rounded-sm transition-colors ${planInterval === "monthly" ? "bg-white text-[#0B4D50] shadow-sm font-medium" : "text-[#6F828A]"}`}
              data-testid="upgrade-toggle-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={planInterval === "yearly"}
              onClick={() => setPlanInterval("yearly")}
              className={`px-4 h-9 text-sm rounded-sm transition-colors inline-flex items-center gap-1.5 ${planInterval === "yearly" ? "bg-white text-[#0B4D50] shadow-sm font-medium" : "text-[#6F828A]"}`}
              data-testid="upgrade-toggle-yearly"
            >
              Yearly
              <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm bg-[#116B6E] text-white font-semibold">
                Save €{YEARLY_SAVINGS_EUR}
              </span>
            </button>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#116B6E] font-medium inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Paid plan · {planInterval === "yearly" ? "Yearly" : "Monthly"}
            </div>
            <div className="mt-2 flex items-baseline gap-1" data-testid={`upgrade-price-display-${planInterval}`}>
              {planInterval === "yearly" ? (
                <>
                  <span className="font-heading text-4xl font-semibold text-[#2E3A3F] tabular-nums">€{YEARLY_PRICE_EUR.toFixed(0)}</span>
                  <span className="text-[#6F828A] text-sm">/ year</span>
                </>
              ) : (
                <>
                  <span className="font-heading text-4xl font-semibold text-[#2E3A3F] tabular-nums">€{MONTHLY_PRICE_EUR.toFixed(2)}</span>
                  <span className="text-[#6F828A] text-sm">/ month</span>
                </>
              )}
            </div>
            {planInterval === "yearly" && (
              <p className="text-sm text-[#0B4D50] mt-1 font-medium" data-testid="upgrade-yearly-equivalent">
                Equivalent to €{YEARLY_MONTHLY_EQUIV} / month — saves €{YEARLY_SAVINGS_EUR} a year
              </p>
            )}
            <div className="mt-3 inline-flex flex-wrap gap-2 text-xs" data-testid="upgrade-billing-terms">
              {planInterval === "yearly" ? (
                <>
                  <span className="px-2.5 py-1 rounded-sm bg-[#E0F2F2] text-[#0B4D50] border border-[#A7D8DA]">Billed once · €{YEARLY_PRICE_EUR.toFixed(0)} upfront</span>
                  <span className="px-2.5 py-1 rounded-sm bg-[#F4F8F9] text-[#2E3A3F] border border-[#D9E1E3]">Cancel anytime, access stays until renewal</span>
                  <span className="px-2.5 py-1 rounded-sm bg-[#F4F8F9] text-[#2E3A3F] border border-[#D9E1E3]">No monthly minimum</span>
                </>
              ) : (
                <>
                  <span className="px-2.5 py-1 rounded-sm bg-[#E0F2F2] text-[#0B4D50] border border-[#A7D8DA]">Minimum 3-month commitment</span>
                  <span className="px-2.5 py-1 rounded-sm bg-[#F4F8F9] text-[#2E3A3F] border border-[#D9E1E3]">Cancel anytime after month 3</span>
                  <span className="px-2.5 py-1 rounded-sm bg-[#F4F8F9] text-[#2E3A3F] border border-[#D9E1E3]">Total minimum charge €{(MONTHLY_PRICE_EUR * 3).toFixed(2)}</span>
                </>
              )}
            </div>
            <p className="text-xs text-[#6F828A] mt-3 leading-relaxed">
              Account-wide — covers your parent account and every child linked to your profile. You're never charged separately per child.
            </p>
          </div>
        </div>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#2E3A3F] pt-2 border-t border-[#EEF3F4]">
          {[
            "Send and receive messages",
            "Log and split expenses",
            "Create and edit calendar events",
            "Create and respond to requests",
            "Upload shared documents",
            "Export PDF records (Messages, Expenses, Journal, Bundle)",
            "All future features",
            planInterval === "yearly"
              ? "Cancel anytime — access continues until your renewal date"
              : "Cancel anytime after your 3rd monthly payment",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-[#1F9FA4] flex-shrink-0 mt-0.5" strokeWidth={2} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="pt-4 border-t border-[#EEF3F4]">
          {isAlreadyPaid ? (
            <div className="space-y-3" data-testid="upgrade-already-paid">
              <div className="text-sm text-[#116B6E] bg-[#E0F2F2] border border-[#A7D8DA] rounded-sm p-3">
                You're already on the {plan.plan === "admin" ? "Admin" : plan.plan === "complimentary" ? "Complimentary" : "Paid"} plan — nothing to do here.
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  onClick={() => navigate("/app/dashboard")}
                  className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm h-10"
                  data-testid="upgrade-already-paid-dashboard-button"
                >
                  Return to dashboard <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/app/settings")}
                  className="border-[#D9E1E3] text-[#2E3A3F] rounded-sm h-10"
                  data-testid="upgrade-already-paid-settings-button"
                >
                  <SettingsIcon className="w-4 h-4 mr-2" /> Go to settings
                </Button>
              </div>
            </div>
          ) : (
            // v77.99.10 — Re-mount the checkout form whenever the
            // interval changes so a stale clientSecret from the other
            // plan never gets confirmed.
            <StripeCheckoutForm
              key={planInterval}
              onSuccess={handlePaymentSuccess}
              planInterval={planInterval}
            />
          )}
        </div>
      </div>

      {/* After-trial explainer (30-day trial → 7-day grace → indefinite lock) */}
      <div className="border border-[#D9E1E3] bg-[#F8FAFA] rounded-md p-5" data-testid="upgrade-after-trial-card">
        <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-2">After your trial</div>
        <p className="text-sm text-[#2E3A3F]">
          If you don't subscribe by the end of your 30-day trial, your account moves to a <strong>7-day read-only grace period</strong>. You can sign in, view every record, review Insights and browse your Court Bundles and Documents — but you can't add new entries, send messages, log expenses, invite co-parents, export PDFs, download reports, generate a Court Bundle or share records externally.
        </p>
        <p className="text-sm text-[#2E3A3F] mt-2">
          After the grace period the account is <strong>locked</strong> until you subscribe. Sign-in still works and shows a clear &ldquo;Subscribe to regain access&rdquo; prompt. Your data is preserved indefinitely — nothing is deleted.
        </p>
        <div className="mt-4 border-t border-[#E4EDEE] pt-4">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-2">Paid-only capabilities</div>
          <ul className="text-sm text-[#2E3A3F] space-y-1 list-disc pl-5" data-testid="upgrade-paid-only-list">
            <li>Exporting PDFs</li>
            <li>Downloading reports</li>
            <li>Generating a Court Bundle</li>
            <li>Exporting data (CSV / bundles)</li>
            <li>Sharing records externally (mediator or solicitor invites)</li>
          </ul>
          <p className="text-[11px] text-[#6F828A] mt-2">Everything else is fully included in the 30-day free trial — no storage limits, no feature caps.</p>
        </div>
      </div>

      <p className="text-[11px] text-[#6F828A]">
        Your co-parent has their own account and their own subscription. One parent subscribed and the other in the read-only grace or locked state is fine — the non-subscribed parent can still sign in and view every record, but can't add new entries, export or share externally until they subscribe.
      </p>
    </div>
  );
}
